/**
 * Centralized Configuration
 * Single source of truth for all config values.
 * All files should import from here — never read import.meta.env directly.
 */

export const config = {
  // API Configuration
  api: {
    baseUrl: import.meta.env.VITE_BACKEND_URL || 'https://api.kaha.com.np/main/api/v3',
    timeout: 30000,
    retryAttempts: 3,
  },

  // Location / Geocoding Configuration
  location: {
    nominatimUrl: import.meta.env.VITE_NOMINATIM_URL || 'https://nominatim.openstreetmap.org',
    defaultCenter: [28.3949, 84.124], // Nepal center
    defaultZoom: 13,
  },

  // Map Tile Configuration
  map: {
    tileLayer: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },

  // Google Maps Configuration
  googleMaps: {
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY_HERE',
    defaultCenter: { lat: 27.7172, lng: 85.3240 }, // Kathmandu
    defaultZoom: 18,
  },

  // External Links
  external: {
    eventsApp: 'https://events.kaha.com.np/',
    mainApp: 'https://www.kaha.com.np/',
  },

  // Upload Constraints
  upload: {
    maxFileSize: 5 * 1024 * 1024, // 5 MB
    allowedFormats: ['image/jpeg', 'image/png', 'image/webp'],
    compressionQuality: 0.8,
  },

  // Feature Flags
  features: {
    enableOfflineMode: false,
    enableCaching: false,
    enableAnalytics: true,
    enableLogin: import.meta.env.VITE_ENABLE_LOGIN !== 'false', // Default true, set VITE_ENABLE_LOGIN=false to disable
  },
};

// ─── Convenience exports ───────────────────────────────────────────────────────
export const API_BASE_URL = config.api.baseUrl;
export const NOMINATIM_URL = config.location.nominatimUrl;

/**
 * Get authorization headers with token.
 * Uses StorageService to avoid direct localStorage access.
 */
export const getAuthHeaders = () => {
  // Imported lazily to avoid circular deps at module load time
  const { storageService } = require('../domain/services/StorageService.js');
  const token = storageService.getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Handle API response errors.
 */
export const handleApiError = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API Error: ${response.status}`);
  }
  return response.json();
};
